import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./DeviceSetup.css";

const DeviceSetup = () => {
  const navigate = useNavigate();
  const [micPermission, setMicPermission] = useState("prompt");
  const [connectionPermission, setConnectionPermission] = useState("prompt");
  const [micDevices, setMicDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [checksStarted, setChecksStarted] = useState(false);
  const [testStatus, setTestStatus] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);

  // Get list of microphone devices
  const getMicrophoneDevices = useCallback(async () => {
    try {
      console.log("Starting device enumeration...");
      // Get all devices - this will only have labels if we already have permission
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log("All devices found:", devices);

      const audioInputDevices = devices.filter(
        (device) =>
          device.kind === "audioinput" && device.deviceId !== "default",
      );

      console.log("Filtered audio input devices:", audioInputDevices);

      // If devices don't have labels, we need to request permission again
      if (audioInputDevices.length > 0 && !audioInputDevices[0].label) {
        console.log(
          "Devices found but no labels - requesting permission again to get labels",
        );
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        console.log("Second stream created for labels");
        // Get devices again after stream is created
        const devicesWithLabels =
          await navigator.mediaDevices.enumerateDevices();
        console.log("All devices after second enumeration:", devicesWithLabels);

        const labeledAudioInputDevices = devicesWithLabels.filter(
          (device) =>
            device.kind === "audioinput" && device.deviceId !== "default",
        );
        stream.getTracks().forEach((track) => track.stop());

        console.log("Devices with labels:", labeledAudioInputDevices);
        setMicDevices(labeledAudioInputDevices);

        // Set default device if available
        if (labeledAudioInputDevices.length > 0 && !selectedDevice) {
          setSelectedDevice(labeledAudioInputDevices[0].deviceId);
          console.log(
            "Default device set:",
            labeledAudioInputDevices[0].deviceId,
          );
        }
      } else {
        setMicDevices(audioInputDevices);
        console.log("Setting mic devices:", audioInputDevices);

        // Set default device if available
        if (audioInputDevices.length > 0 && !selectedDevice) {
          setSelectedDevice(audioInputDevices[0].deviceId);
          console.log("Default device set:", audioInputDevices[0].deviceId);
        }
      }
    } catch (err) {
      console.error("Error getting microphone devices:", err);
      if (err.name === "NotAllowedError") {
        setError(
          "Cannot access microphone devices. Please grant microphone permission first.",
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "No microphone devices found. Please connect a microphone and refresh the page.",
        );
      } else {
        setError(`Failed to get microphone devices: ${err.message}`);
      }
    }
  }, [selectedDevice]);

  // Check microphone permission
  const checkMicrophonePermission = useCallback(async () => {
    try {
      console.log("Starting microphone permission check...");
      // First try to get user media - this is what actually triggers device enumeration
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      console.log("Microphone permission granted, stream created");
      setMicPermission("granted");

      // Get devices after we have permission and a stream
      await getMicrophoneDevices();

      // Stop the stream immediately since we only needed permission
      stream.getTracks().forEach((track) => track.stop());
      console.log("Stream stopped");

      // Also check permission status if supported
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone",
        });
        console.log("Permission API status:", permissionStatus.state);
        setMicPermission(permissionStatus.state);

        permissionStatus.onchange = () => {
          setMicPermission(permissionStatus.state);
        };
      } catch (permErr) {
        console.log(
          "Permission API not supported, using getUserMedia fallback",
        );
        // Permission API not supported, but we already have getUserMedia working
      }
    } catch (err) {
      console.error("Failed to get microphone permission:", err);
      setMicPermission("denied");
      if (err.name === "NotAllowedError") {
        setError(
          "Microphone access was denied. Please allow microphone access in your browser settings and try again.",
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "No microphone found. Please connect a microphone and try again.",
        );
      } else if (err.name === "NotSupportedError") {
        setError(
          "Your browser doesn't support microphone access. Please try a modern browser like Chrome, Firefox, or Edge.",
        );
      } else {
        setError(`Failed to access microphone: ${err.message}`);
      }
    }
  }, [getMicrophoneDevices]);

  // Check connection permission (navigator.connection)
  const checkConnectionPermission = useCallback(() => {
    if ("connection" in navigator) {
      const connection = navigator.connection;
      setConnectionPermission("granted");

      // Monitor connection changes
      const handleConnectionChange = () => {
        console.log("Connection changed:", connection);
      };

      connection.addEventListener("change", handleConnectionChange);
      return () =>
        connection.removeEventListener("change", handleConnectionChange);
    } else {
      setConnectionPermission("unsupported");
    }
  }, []);

  // Request microphone permission
  const requestMicrophonePermission = async () => {
    setIsLoading(true);
    setError("");

    try {
      // This will trigger both permission and device enumeration
      await checkMicrophonePermission();
    } catch (err) {
      // Error already handled in checkMicrophonePermission
    } finally {
      setIsLoading(false);
    }
  };

  // Record audio from microphone
  const startRecording = async () => {
    if (!selectedDevice) {
      setError("Please select a microphone first");
      return;
    }

    setIsLoading(true);
    setError("");
    setTestStatus("Preparing to record...");
    setRecordedAudio(null);
    setRecordingTime(0);

    try {
      const constraints = {
        audio: {
          deviceId: selectedDevice ? { exact: selectedDevice } : undefined,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100,
          channelCount: 1,
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        setIsRecording(false);
        setIsLoading(false);
        setTestStatus(
          "Recording complete! Click 'Play Recording' to hear yourself.",
        );

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      // Start recording for 5 seconds
      mediaRecorder.start();
      setIsRecording(true);
      setTestStatus("🎤 Recording... Speak into your microphone now!");

      let timeLeft = 5;
      setRecordingTime(timeLeft);

      const timer = setInterval(() => {
        timeLeft--;
        setRecordingTime(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(timer);
          mediaRecorder.stop();
        }
      }, 1000);
    } catch (err) {
      console.error("Recording failed:", err);
      if (err.name === "NotAllowedError") {
        setError(
          "Microphone access denied. Please allow microphone access to record.",
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "Selected microphone not found. Please select a different device.",
        );
      } else if (err.name === "NotReadableError") {
        setError(
          "Microphone is in use by another application. Please close other apps using the microphone.",
        );
      } else {
        setError(`Failed to start recording: ${err.message}`);
      }
      setIsLoading(false);
      setIsRecording(false);
      setTestStatus("");
    }
  };

  // Play recorded audio
  const playRecording = () => {
    if (!recordedAudio) return;

    const audio = new Audio(recordedAudio);
    setIsPlaying(true);
    setTestStatus("Playing your recording...");

    audio.onended = () => {
      setIsPlaying(false);
      setTestStatus("Playback complete!");
      setTimeout(() => setTestStatus(""), 2000);
    };

    audio.onerror = () => {
      setIsPlaying(false);
      setTestStatus("Failed to play recording");
    };

    audio.play();
  };

  // Initialize permissions and devices only after user clicks Start
  useEffect(() => {
    if (checksStarted) {
      console.log("Checks started, initiating device checks...");
      checkMicrophonePermission();
      checkConnectionPermission();

      // Listen for device changes
      if (navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener(
          "devicechange",
          getMicrophoneDevices,
        );
        console.log("Added devicechange listener");
      }

      return () => {
        if (navigator.mediaDevices) {
          navigator.mediaDevices.removeEventListener(
            "devicechange",
            getMicrophoneDevices,
          );
          console.log("Removed devicechange listener");
        }
      };
    }
  }, [
    checkMicrophonePermission,
    checkConnectionPermission,
    getMicrophoneDevices,
    checksStarted,
  ]);

  // Get permission status display
  const getPermissionStatusDisplay = (status) => {
    const statusMap = {
      granted: { text: "Granted", className: "status-granted" },
      denied: { text: "Denied", className: "status-denied" },
      prompt: { text: "Not Requested", className: "status-prompt" },
      unsupported: { text: "Unsupported", className: "status-unsupported" },
    };

    return (
      statusMap[status] || { text: "Unknown", className: "status-unknown" }
    );
  };

  const micStatus = getPermissionStatusDisplay(micPermission);
  const connectionStatus = getPermissionStatusDisplay(connectionPermission);

  // Start the device checks
  const startChecks = () => {
    console.log("Start button clicked, initiating checks...");
    setChecksStarted(true);
  };

  return (
    <div className="device-setup">
      <div className="device-setup__header">
        <div className="device-setup__header-top">
          <button
            className="device-setup__back-btn"
            onClick={() => navigate("/")}
            title="Back to Main"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 19L5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
        </div>
        <h1>Device Setup</h1>
        <p>
          Configure your microphone and connection settings for optimal call
          quality
        </p>
      </div>

      <div className="device-setup__content">
        {!checksStarted ? (
          <div className="start-section">
            <h2>Ready to Check Your Devices?</h2>
            <p>
              Click the Start button below to begin checking your microphone
              permissions and available devices. This will request access to
              your microphone.
            </p>
            <button className="btn btn-primary btn-large" onClick={startChecks}>
              Start Device Check
            </button>
          </div>
        ) : (
          <>
            {/* Microphone Permission Section */}
            <div className="permission-section">
              <h2>Microphone Permissions</h2>
              <div className="permission-status">
                <span className={`status-badge ${micStatus.className}`}>
                  {micStatus.text}
                </span>
                {micPermission === "denied" && (
                  <p className="permission-help">
                    Microphone access is blocked. Please allow microphone access
                    in your browser settings.
                  </p>
                )}
                {micPermission === "prompt" && (
                  <button
                    className="btn btn-primary"
                    onClick={requestMicrophonePermission}
                    disabled={isLoading}
                  >
                    {isLoading ? "Requesting..." : "Allow Microphone Access"}
                  </button>
                )}
              </div>
            </div>

            {/* Connection Permission Section */}
            <div className="permission-section">
              <h2>Connection Status</h2>
              <div className="permission-status">
                <span className={`status-badge ${connectionStatus.className}`}>
                  {connectionStatus.text}
                </span>
                {connectionPermission === "unsupported" && (
                  <p className="permission-help">
                    Connection API not supported in this browser.
                  </p>
                )}
              </div>
            </div>

            {/* Microphone Devices Section */}
            <div className="devices-section">
              <h2>Available Microphones</h2>
              {micPermission === "granted" ? (
                <div className="devices-list">
                  {micDevices.length === 0 ? (
                    <p>
                      No microphone devices found. Please check your device
                      connections.
                    </p>
                  ) : (
                    <>
                      <select
                        className="device-select"
                        value={selectedDevice}
                        onChange={(e) => setSelectedDevice(e.target.value)}
                      >
                        {micDevices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label ||
                              `Microphone ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>

                      <div className="recording-controls">
                        {!recordedAudio ? (
                          <button
                            className={`btn btn-primary ${isRecording ? "recording-active" : ""}`}
                            onClick={startRecording}
                            disabled={
                              isLoading || isRecording || !selectedDevice
                            }
                          >
                            {isRecording ? (
                              <>🔴 Recording... {recordingTime}s</>
                            ) : (
                              "🎤 Start Recording (5s)"
                            )}
                          </button>
                        ) : (
                          <div className="playback-controls">
                            <button
                              className="btn btn-secondary"
                              onClick={playRecording}
                              disabled={isPlaying}
                            >
                              {isPlaying
                                ? "▶️ Playing..."
                                : "▶️ Play Recording"}
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={startRecording}
                              disabled={isLoading || isRecording}
                            >
                              🔄 Record Again
                            </button>
                          </div>
                        )}
                      </div>
                      {testStatus && (
                        <div className="test-status">{testStatus}</div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p>Grant microphone permission to see available devices.</p>
              )}
            </div>

            {/* Error Display */}
            {error && <div className="error-message">{error}</div>}

            {/* Setup Complete Section */}
            {micPermission === "granted" && micDevices.length > 0 && (
              <div className="setup-complete">
                <h3>✅ Setup Complete</h3>
                <p>Your microphone is configured and ready for calls.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceSetup;
